/**
 * Limpa o cache do TanStack Query quando a sessão é encerrada (GUPM-241).
 *
 * O logout do app é navegação SPA — não há reload —, então sem isto o cache
 * sobrevive à troca de usuário e o plantonista seguinte herda dados do anterior
 * (TARPs, contatos, identidade). No posto de vigilância o dispositivo é
 * compartilhado: trocar de usuário na mesma aba é o caso normal.
 *
 * **Ancorado no evento `SIGNED_OUT`, não no `signOut()`.** O `signOut()` pode
 * retornar erro *sem* encerrar a sessão (ver o comentário em `useAuth`); limpar
 * ali apagaria o cache de uma sessão que continua válida. O evento só chega
 * quando a saída ocorreu de fato — e cobre saída disparada em outra aba.
 *
 * Fica FORA do `useAuth` de propósito: ele tem 13 call sites (GUPM-237) e
 * `useQueryClient()` lança sem `QueryClientProvider`, o que quebraria testes que
 * montam consumidores de `useAuth` com `render` puro. Deve ser chamado UMA vez,
 * na raiz da árvore (`App`).
 */
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useNotificacoesStore } from '@/stores/notificacoesStore';

export function useClearCacheOnSignOut(): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event) => {
      // Só a saída. `TOKEN_REFRESHED` chega de rotina e limpar nele faria a tela
      // recarregar tudo sozinha a cada renovação de token.
      if (event !== 'SIGNED_OUT') return;
      queryClient.clear();
      // C-24 — os "lidos" do sino também são da sessão que saiu.
      useNotificacoesStore.getState().limpar();
    });
    return () => subscription.unsubscribe();
  }, [queryClient]);
}
